// src/services/duplicateDismissalService.js
//
// "Not a duplicate" decisions from the Scan for Duplicates page, shared by
// everyone at the site: duplicate_dismissals/{pairKey}, one doc per pair of
// records staff said are different people (pairKeyOf in duplicateScan.js).
//
// Holds record ids (inside the doc id), who and when: never a name, DOB or
// MRN. Never deleted (rules): "Show again" sets status "restored", so the
// decision history stays on the doc.

import { collection, doc, getDocs, serverTimestamp, writeBatch } from "firebase/firestore";
import { auth, db } from "../firebase";
import { getCollectionPath } from "./firestorePaths";

const dismissalsPath = () => getCollectionPath("duplicate_dismissals");
const me = () => (auth.currentUser && auth.currentUser.email) || "unknown";

/** @returns {Promise<Map<string, {dismissedBy:string, dismissedAt:any}>>} pairs currently dismissed */
export async function loadDismissals() {
  const snap = await getDocs(collection(db, dismissalsPath()));
  const out = new Map();
  snap.docs.forEach((d) => {
    const data = d.data();
    if (data.status === "dismissed") out.set(d.id, data);
  });
  return out;
}

// Firestore batches hold at most 500 writes.
async function writePairs(pairKeys, fields) {
  const keys = [...new Set((pairKeys || []).filter(Boolean))];
  for (let i = 0; i < keys.length; i += 450) {
    const batch = writeBatch(db);
    keys.slice(i, i + 450).forEach((k) => batch.set(doc(db, dismissalsPath(), k), fields, { merge: true }));
    await batch.commit();
  }
}

/** Mark these pairs "Not a duplicate" for everyone at the site. */
export const dismissPairs = (pairKeys) =>
  writePairs(pairKeys, { status: "dismissed", dismissedBy: me(), dismissedAt: serverTimestamp() });

/** "Show again": the pairs come back into the scan. */
export const restorePairs = (pairKeys) =>
  writePairs(pairKeys, { status: "restored", restoredBy: me(), restoredAt: serverTimestamp() });
